<script setup lang="ts">
  import { computed, useCssModule } from 'vue';
  import type { RouteLocationRaw } from 'vue-router';

  import type { IconName } from '../Icon/Icon.types';
  import Icon from '../Icon/Icon.vue';

  export interface QuickActionProps {
    /**
     * The title prop will show the first line of text
     */
    title: string;

    /**
     * The subtitle prop will show the second line of text
     */
    subtitle: string;

    /**
     * The `to` prop for vue-router's `RouterLink` component
     */
    to?: RouteLocationRaw;

    /**
     * If defined, the quick action will render as an `<a />` tag.
     */
    href?: string;

    /**
     * Icon to render on the left side
     */
    icon: IconName;
  }

  const props = defineProps<QuickActionProps>();
  const classes = useCssModule();

  const is = computed(() => {
    if (props.to) {
      return 'router-link';
    }

    if (props.href) {
      return 'a';
    }

    return 'div';
  });

  const linkAttrs = computed(() => {
    if (props.to) {
      return {
        to: props.to,
      };
    }

    if (props.href) {
      return {
        href: props.href,
      };
    }

    return {};
  });
</script>

<template>
  <component
    :is="is"
    v-bind="linkAttrs"
    class="stash-quick-action rounded p-3 shadow"
    :class="classes.root"
    data-test="stash-quick-action"
  >
    <div class="flex items-center gap-3">
      <div class="flex items-center justify-center rounded bg-blue-100 p-3">
        <Icon :name="props.icon" class="text-blue-500" />
      </div>
      <div class="flex-1">
        <h4 class="text-blue-500">{{ props.title }}</h4>
        <p class="text-xs font-normal text-ice-700 lg:mb-0">
          {{ props.subtitle }}
        </p>
      </div>
      <Icon name="arrow-right" class="text-blue-500" />
    </div>
  </component>
</template>

<style module>
  .root {
    background-color: #fff;
    border: 1px solid transparent; /* it prevents layout size shifting of extra height/width when hover */
    cursor: pointer;
    display: inline-block;
  }

  .root:hover,
  .root:active,
  .root:focus {
    border: 1px solid var(--color-blue-500);
    text-decoration: none;
  }
</style>
